import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { Images, Trash, Upload, Sparkle, PaintBrush, CircleNotch, Copy, Check, Plus, X, ImageSquare, MagicWand, DownloadSimple, Briefcase, House, VideoCamera, Car, Tree, Dress, Heartbeat, Coffee, Microphone, Airplane, Broadcast, Storefront, Snowflake, Moon, Crown, ArrowsClockwise } from "@phosphor-icons/react";
import { API, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { downloadFromUrl } from "@/components/shared";
import {
  SCENE_CATEGORIES,
  SCENE_PROMPTS,
  REALISM_MODIFIER,
  INDUSTRY_SUGGESTIONS,
  getScenesByCategory,
} from "@/data/scenePrompts";

const SCENE_ICON_MAP = {
  Briefcase, House, VideoCamera, Car, Tree, Dress, Heartbeat, Coffee,
  Microphone, Airplane, Broadcast, Storefront, Snowflake, Moon, MagicWand, Crown,
};

const prioritizedCategoryIds = (industry) => {
  const preferred = INDUSTRY_SUGGESTIONS[industry] || [];
  const rest = SCENE_CATEGORIES.filter((c) => !preferred.includes(c.id)).map((c) => c.id);
  return [...preferred, ...rest];
};

const TEXT_MODELS = [
  { id: "nano-banana-2", name: "Nano Banana 2", desc: "Fast, 4K output" },
  { id: "nano-banana-pro", name: "Nano Banana Pro", desc: "Higher quality" },
];

const REFERENCE_MODELS = [
  { id: "flux-kontext-pro-i2i", name: "Flux Kontext Pro", desc: "Best face consistency & flexible content policies", recommended: true },
  { id: "nano-banana-2-edit", name: "Nano Banana 2 Edit", desc: "Fast editing, great for outfit swaps" },
  { id: "nano-banana-pro-edit", name: "Nano Banana Pro Edit", desc: "Higher quality editing" },
];

// ============= GALLERY TAB =============
const GalleryTab = ({ images, selectedImage, setSelectedImage, fetchImages, uploading, setUploading }) => {
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("description", "");
    formData.append("tags", "");
    try {
      await axios.post(`${API}/ai-twin/images/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      fetchImages();
    } catch (e) {
      console.error("Error uploading:", e);
    } finally {
      setUploading(false);
    }
  };

  const deleteImage = async (id) => {
    try {
      await axios.delete(`${API}/ai-twin/images/${id}`);
      if (selectedImage?.id === id) setSelectedImage(null);
      fetchImages();
    } catch (e) {
      console.error("Error deleting:", e);
    }
  };

  const [copied, setCopied] = useState(null);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-zinc-500">{images.length} image{images.length !== 1 ? "s" : ""} in your gallery</p>
        <label className="btn-primary flex items-center gap-2 cursor-pointer text-sm px-4 py-2" data-testid="upload-image-btn">
          <Upload size={16} />
          {uploading ? "Uploading..." : "Upload Image"}
          <input type="file" accept="image/*" onChange={handleUpload} className="hidden" disabled={uploading} data-testid="upload-image-input" />
        </label>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
        {images.map((image) => (
          <div
            key={image.id}
            className={`relative aspect-[3/4] rounded-xl overflow-hidden group cursor-pointer border-2 transition-all ${
              selectedImage?.id === image.id ? "border-[#E91E8C] ring-2 ring-[#E91E8C]/30" : "border-transparent hover:border-zinc-200"
            }`}
            onClick={() => setSelectedImage(selectedImage?.id === image.id ? null : image)}
            data-testid={`gallery-image-${image.id}`}
          >
            <img src={`${BACKEND_URL}/api/ai-twin/images/${image.filename}`} alt={image.original_name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-all">
              <div className="absolute bottom-0 left-0 right-0 p-2.5">
                <p className="text-white text-xs truncate">{image.original_name}</p>
                {(image.source === "generated" || image.source === "reference") && (
                  <span className="text-[9px] bg-[#E91E8C]/80 text-white px-1.5 py-0.5 rounded-full mt-0.5 inline-block">
                    {image.source === "reference" ? "AI + Reference" : "AI Generated"}
                  </span>
                )}
                {image.source === "edited" && <span className="text-[9px] bg-purple-500/80 text-white px-1.5 py-0.5 rounded-full mt-0.5 inline-block">AI Edited</span>}
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); downloadFromUrl(`${BACKEND_URL}/api/ai-twin/images/${image.filename}`, image.original_name || image.filename); }}
                className="absolute top-2 left-2 p-1.5 bg-white/90 text-zinc-700 rounded-full opacity-0 group-hover:opacity-100 transition-all hover:bg-white"
                data-testid={`download-image-${image.id}`}
                title="Download"
              >
                <DownloadSimple size={12} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); deleteImage(image.id); }}
                className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-all hover:bg-red-600"
                data-testid={`delete-image-${image.id}`}
              >
                <Trash size={12} />
              </button>
            </div>
            {selectedImage?.id === image.id && (
              <div className="absolute top-2 left-2 bg-[#E91E8C] text-white text-[10px] px-2 py-0.5 rounded-full font-semibold">Selected</div>
            )}
          </div>
        ))}
      </div>

      {images.length === 0 && (
        <div className="text-center py-16 text-zinc-400">
          <Images size={48} className="mx-auto mb-3 text-zinc-300" />
          <p className="font-medium">No AI Twin images yet</p>
          <p className="text-sm mt-1">Upload photos or generate new ones with AI</p>
        </div>
      )}

      {selectedImage && (
        <div className="card p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={`${BACKEND_URL}/api/ai-twin/images/${selectedImage.filename}`} alt="" className="w-14 h-14 rounded-lg object-cover" />
            <div>
              <p className="font-semibold text-zinc-900 text-sm">{selectedImage.original_name}</p>
              <p className="text-xs text-zinc-400">Ready to use in Auto Create</p>
            </div>
          </div>
          <button
            onClick={() => {
              navigator.clipboard.writeText(`${BACKEND_URL}/api/ai-twin/images/${selectedImage.filename}`);
              setCopied(selectedImage.id);
              setTimeout(() => setCopied(null), 2000);
            }}
            className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-zinc-200 hover:bg-zinc-50 transition-all"
            data-testid="copy-image-url"
          >
            {copied === selectedImage.id ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
            {copied === selectedImage.id ? "Copied" : "Copy URL"}
          </button>
        </div>
      )}
    </div>
  );
};

// ============= CREATE TAB (Unified Generate + Edit + Scenes) =============
const CreateTab = ({ images, fetchImages, userIndustry }) => {
  const [prompt, setPrompt] = useState("");
  const [referenceImages, setReferenceImages] = useState([]); // {id, filename, original_name, fromGallery}
  const [model, setModel] = useState("nano-banana-2");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showGalleryPicker, setShowGalleryPicker] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const outfitInputRef = useRef(null);
  const [outfitFile, setOutfitFile] = useState(null);
  const [outfitPreview, setOutfitPreview] = useState(null);
  // Product reference image — lets UGC creators put a real product into
  // the twin's hands (bottle, box, tube, device, etc.). Optional third input
  // alongside the scene prompt + outfit. Uses the same upload/remove pattern.
  const productInputRef = useRef(null);
  const [productFile, setProductFile] = useState(null);
  const [productPreview, setProductPreview] = useState(null);

  // Scene picker state (only usable when a reference image is selected)
  const [activeCategory, setActiveCategory] = useState(null);
  const [pickedSceneId, setPickedSceneId] = useState(null);
  const [addRealism, setAddRealism] = useState(true);
  // Tracks scene IDs this user has already generated from, so we can mark
  // them with a "Used" badge AND de-prioritize them when shuffling.
  const [usedSceneIds, setUsedSceneIds] = useState([]);
  // Bumping this int triggers a reshuffle of the visible order — we use
  // Math.random() keyed by this number so each refresh produces a new order.
  const [shuffleKey, setShuffleKey] = useState(0);

  const scenesByCat = useMemo(() => getScenesByCategory(), []);
  const orderedCategories = useMemo(() => {
    const ids = prioritizedCategoryIds(userIndustry);
    return ids.map((id) => SCENE_CATEGORIES.find((c) => c.id === id)).filter(Boolean);
  }, [userIndustry]);

  useEffect(() => {
    if (!activeCategory && orderedCategories.length > 0) {
      setActiveCategory(orderedCategories[0].id);
    }
  }, [orderedCategories, activeCategory]);

  // Load which scene presets the user has already used (persisted server-side)
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/ai-twin/scene-usage`);
        setUsedSceneIds(res.data?.used_scene_ids || []);
      } catch (_) {
        /* non-fatal: just hide the "Used" marker */
      }
    })();
  }, []);

  // Show at most this many presets per category at a time. Refresh rotates
  // through fresh unused ones first, then shuffles from the rest.
  const PRESETS_PER_PAGE = 24;

  const visibleScenes = useMemo(() => {
    const pool = (scenesByCat[activeCategory] || []).filter(Boolean);
    if (!pool.length) return [];
    const usedSet = new Set(usedSceneIds);
    // Pseudo-random Fisher-Yates keyed on shuffleKey + category so the order
    // is stable across rerenders but changes every time the user hits Refresh.
    const seed = `${activeCategory}-${shuffleKey}`;
    let h = 0;
    for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
    const rand = () => {
      h = (h * 9301 + 49297) % 233280;
      return h / 233280;
    };
    const shuffled = pool.slice();
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(rand() * (i + 1));
      const tmp = shuffled[i];
      shuffled[i] = shuffled[j];
      shuffled[j] = tmp;
    }
    // Unused first, then used at the end so refresh-hungry users keep seeing
    // new stuff before the app starts repeating their old picks.
    const unused = shuffled.filter((s) => s && !usedSet.has(s.id));
    const used = shuffled.filter((s) => s && usedSet.has(s.id));
    return [...unused, ...used].slice(0, PRESETS_PER_PAGE);
  }, [scenesByCat, activeCategory, usedSceneIds, shuffleKey]);

  const refreshScenes = () => setShuffleKey((k) => k + 1);

  const handlePickScene = (scene) => {
    setPickedSceneId(scene.id);
    setPrompt(scene.prompt);
    setError(null);
  };

  // Product reveal video: after a product-held generation, user can kick off
  // a short Seedance I2V pass to animate the twin subtly presenting the
  // product to camera. Separate state because the base-videos endpoint is
  // async (poll), and we want the button on the result card to be self-contained.
  const [revealJob, setRevealJob] = useState(null); // {status, name, filename, id, error?}
  const [revealLoading, setRevealLoading] = useState(false);

  const handleGenerateProductReveal = async () => {
    if (!result?.image?.id) return;
    setRevealLoading(true);
    setRevealJob(null);
    try {
      const res = await axios.post(`${API}/base-videos/generate-idle`, {
        image_id: result.image.id,
        mode: "product_reveal",
      });
      const v = res.data?.video;
      if (res.data?.cached && v?.status === "ready") {
        setRevealJob(v);
        setRevealLoading(false);
        return;
      }
      // Poll the base_videos doc until ready — Seedance takes 60–180s typically
      let attempts = 0;
      const maxAttempts = 180; // 180 * 5s = 15 min
      const pollId = v?.id;
      while (attempts < maxAttempts && pollId) {
        await new Promise((r) => setTimeout(r, 5000));
        attempts++;
        try {
          const listRes = await axios.get(`${API}/base-videos`);
          const fresh = (listRes.data?.videos || []).find((x) => x.id === pollId);
          if (fresh?.status === "ready") {
            setRevealJob(fresh);
            setRevealLoading(false);
            return;
          }
          if (fresh?.status === "failed") {
            setRevealJob({ ...fresh, error: fresh.error || "Reveal clip failed" });
            setRevealLoading(false);
            return;
          }
        } catch (_) { /* keep polling */ }
      }
      setRevealJob({ status: "failed", error: "Product reveal timed out. Please try again." });
    } catch (e) {
      setRevealJob({ status: "failed", error: e.response?.data?.detail || "Reveal generation failed" });
    } finally {
      setRevealLoading(false);
    }
  };

  const hasReference = referenceImages.length > 0;
  const activeModels = hasReference ? REFERENCE_MODELS : TEXT_MODELS;

  // Reset model when switching modes
  useEffect(() => {
    if (hasReference) {
      // Default to Nano Banana edit when outfit is uploaded, Flux otherwise
      if (outfitFile) {
        setModel("nano-banana-2-edit");
      } else {
        setModel("flux-kontext-pro-i2i");
      }
    } else {
      setModel("nano-banana-2");
    }
  }, [hasReference, outfitFile]);

  const addGalleryImage = (img) => {
    if (referenceImages.find(r => r.id === img.id)) return;
    setReferenceImages([{ id: img.id, filename: img.filename, original_name: img.original_name, fromGallery: true }]);
    setShowGalleryPicker(false);
  };

  const handleFileDrop = async (files) => {
    const file = files[0];
    if (!file || !file.type.startsWith("image/")) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("description", "Reference upload");
    formData.append("tags", "reference");
    try {
      const res = await axios.post(`${API}/ai-twin/images/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const uploaded = res.data.image;
      setReferenceImages([{ id: uploaded.id, filename: uploaded.filename, original_name: uploaded.original_name || file.name, fromGallery: false }]);
      fetchImages();
    } catch (e) {
      console.error("Upload error:", e);
    } finally {
      setUploading(false);
    }
  };

  const removeReference = () => {
    setReferenceImages([]);
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    setError(null);
    setResult(null);
    setRevealJob(null);
    try {
      let fullPrompt = prompt.trim();
      let outfitImageId = null;
      let productImageId = null;

      // Upload outfit image first and get its ID
      if (outfitFile && hasReference) {
        const formData = new FormData();
        formData.append("file", outfitFile);
        formData.append("description", "Outfit reference");
        formData.append("tags", "outfit");
        const uploadRes = await axios.post(`${API}/ai-twin/images/upload`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        outfitImageId = uploadRes.data.image.id;
        fullPrompt = `Dress the person from the first image in the exact outfit shown in the second image. ${fullPrompt}`;
      }

      // Upload product image (UGC creators placing a product in the twin's hand)
      // and append an instruction that references the correct slot. The slot
      // index changes based on whether outfit is also present because the
      // backend passes all images into a single `images_list` array, so the
      // AI model needs to know which ordinal the product lives at.
      if (productFile && hasReference) {
        const formData = new FormData();
        formData.append("file", productFile);
        formData.append("description", "Product reference");
        formData.append("tags", "product");
        const uploadRes = await axios.post(`${API}/ai-twin/images/upload`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        productImageId = uploadRes.data.image.id;
        const slotLabel = outfitImageId ? "third image" : "second image";
        fullPrompt = `${fullPrompt} The person is holding the exact product shown in the ${slotLabel} — preserve the product's label, shape, color, and texture precisely. Place it naturally in one hand at chest or waist level, with correct lighting and shadows.`;
      }

      // Append realism boost when generating a scene with a reference image
      if (hasReference && addRealism) {
        fullPrompt = `${fullPrompt}\n\n${REALISM_MODIFIER}`;
      }

      const payload = {
        prompt: fullPrompt,
        model,
      };
      if (hasReference) {
        payload.reference_image_id = referenceImages[0].id;
      }
      if (outfitImageId) {
        payload.outfit_image_id = outfitImageId;
      }
      if (productImageId) {
        payload.product_image_id = productImageId;
      }

      // Start the job
      const startRes = await axios.post(`${API}/ai-twin/generate`, payload);
      const jobId = startRes.data.job_id;
      if (!jobId) throw new Error("No job_id returned");

      // Fire-and-forget: record that this scene preset was used so the UI
      // can mark it with a "Used" badge and the refresh shuffle can push
      // fresh presets to the top next time.
      if (pickedSceneId) {
        const usedId = pickedSceneId;
        axios
          .post(`${API}/ai-twin/scene-usage/mark`, { scene_id: usedId })
          .then(() => setUsedSceneIds((prev) => (prev.includes(usedId) ? prev : [...prev, usedId])))
          .catch(() => {});
      }

      // Poll for completion
      let attempts = 0;
      const maxAttempts = 120; // 120 * 2.5s = 5 min max
      while (attempts < maxAttempts) {
        await new Promise(r => setTimeout(r, 2500));
        attempts++;
        try {
          const pollRes = await axios.get(`${API}/ai-twin/generate/status/${jobId}`);
          const job = pollRes.data;
          if (job.status === "completed" && job.image) {
            setResult(job);
            fetchImages();
            setGenerating(false);
            return;
          } else if (job.status === "failed") {
            setError(job.error || "Generation failed");
            setGenerating(false);
            return;
          }
          // Still processing — continue polling
        } catch (pollErr) {
          console.warn("Poll error:", pollErr);
        }
      }
      setError("Generation timed out. Please try again.");
    } catch (e) {
      setError(e.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="card p-5 space-y-4">
        {/* Reference Image Area */}
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-2">
            Reference Image <span className="text-zinc-300 font-normal normal-case">(optional — upload your photo to keep your likeness)</span>
          </label>

          {hasReference ? (
            <div className="flex items-center gap-3 p-3 bg-zinc-50 rounded-xl border border-zinc-200">
              <div className="relative">
                <img
                  src={`${BACKEND_URL}/api/ai-twin/images/${referenceImages[0].filename}`}
                  alt=""
                  className="w-20 h-20 rounded-lg object-cover"
                  data-testid="reference-image-preview"
                />
                <button
                  onClick={removeReference}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-all"
                  data-testid="remove-reference-btn"
                >
                  <X size={10} weight="bold" />
                </button>
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-zinc-800">{referenceImages[0].original_name}</p>
                <p className="text-xs text-[#E91E8C] font-medium mt-0.5">Reference mode — AI will use your photo as a base</p>
              </div>
            </div>
          ) : (
            <div
              className={`border-2 border-dashed rounded-xl p-5 text-center transition-all cursor-pointer ${
                dragOver ? "border-[#E91E8C] bg-[#E91E8C]/5" : "border-zinc-200 hover:border-zinc-300"
              }`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFileDrop(e.dataTransfer.files); }}
              onClick={() => fileInputRef.current?.click()}
              data-testid="reference-drop-zone"
            >
              {uploading ? (
                <div className="flex items-center justify-center gap-2 text-[#E91E8C]">
                  <CircleNotch size={20} className="animate-spin" />
                  <span className="text-sm font-medium">Uploading...</span>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-zinc-100 flex items-center justify-center">
                      <Upload size={18} className="text-zinc-400" />
                    </div>
                    <span className="text-zinc-300 text-sm">or</span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setShowGalleryPicker(true); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-100 rounded-lg text-sm font-medium text-zinc-600 hover:bg-zinc-200 transition-all"
                      data-testid="pick-from-gallery-btn"
                    >
                      <ImageSquare size={14} />
                      Pick from Gallery
                    </button>
                  </div>
                  <p className="text-xs text-zinc-400">Drag & drop your photo here, or click to upload</p>
                  <p className="text-[10px] text-zinc-300 mt-1">Without a reference photo, AI will generate from your text prompt only</p>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => e.target.files && handleFileDrop(e.target.files)}
                data-testid="reference-file-input"
              />
            </div>
          )}

          {/* Gallery Picker Modal */}
          {showGalleryPicker && (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowGalleryPicker(false)}>
              <div className="bg-white rounded-2xl p-5 max-w-lg w-full max-h-[70vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="gallery-picker-modal">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-zinc-900">Pick from Gallery</h3>
                  <button onClick={() => setShowGalleryPicker(false)} className="p-1 hover:bg-zinc-100 rounded-lg">
                    <X size={18} />
                  </button>
                </div>
                {images.length === 0 ? (
                  <p className="text-sm text-zinc-400 py-8 text-center">No images in gallery yet</p>
                ) : (
                  <div className="grid grid-cols-4 gap-2">
                    {images.map((img) => (
                      <button
                        key={img.id}
                        type="button"
                        onClick={() => addGalleryImage(img)}
                        className="aspect-square rounded-lg overflow-hidden border-2 border-zinc-200 hover:border-[#E91E8C] transition-all"
                        data-testid={`gallery-pick-${img.id}`}
                      >
                        <img src={`${BACKEND_URL}/api/ai-twin/images/${img.filename}`} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Outfit Image Upload (when reference mode) */}
        {hasReference && (
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-2">
              Upload Outfit <span className="text-zinc-300 font-normal normal-case">(optional — drag, drop, paste, or upload)</span>
            </label>
            <div className="flex gap-3">
              <div
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("border-[#E91E8C]", "bg-[#E91E8C]/5"); }}
                onDragLeave={(e) => { e.currentTarget.classList.remove("border-[#E91E8C]", "bg-[#E91E8C]/5"); }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove("border-[#E91E8C]", "bg-[#E91E8C]/5");
                  const file = e.dataTransfer.files?.[0];
                  if (file && file.type.startsWith("image/")) {
                    setOutfitFile(file);
                    setOutfitPreview(URL.createObjectURL(file));
                  }
                }}
                onPaste={(e) => {
                  const items = e.clipboardData?.items;
                  if (items) {
                    for (const item of items) {
                      if (item.type.startsWith("image/")) {
                        const file = item.getAsFile();
                        if (file) {
                          setOutfitFile(file);
                          setOutfitPreview(URL.createObjectURL(file));
                        }
                        break;
                      }
                    }
                  }
                }}
                tabIndex={0}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-4 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                  outfitPreview ? "border-green-300 bg-green-50" : "border-zinc-200 hover:border-[#E91E8C] hover:bg-[#E91E8C]/5"
                }`}
                data-testid="create-outfit-upload-btn"
                onClick={() => outfitInputRef.current?.click()}
              >
                <input
                  ref={outfitInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setOutfitFile(file);
                      setOutfitPreview(URL.createObjectURL(file));
                    }
                  }}
                  data-testid="create-outfit-file-input"
                />
                {outfitPreview ? (
                  <div className="flex items-center gap-3">
                    <img src={outfitPreview} alt="Outfit" className="w-14 h-14 object-cover rounded-lg border border-green-300" />
                    <div className="text-left">
                      <p className="text-sm font-medium text-green-700">Outfit uploaded</p>
                      <p className="text-xs text-green-600">Drop, paste, or click to change</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <Upload size={20} className="mx-auto text-zinc-400 mb-1" />
                    <p className="text-sm text-zinc-600 font-medium">Upload Outfit Image</p>
                    <p className="text-[10px] text-zinc-400 mt-0.5">Drag & drop, paste, or click to browse</p>
                  </div>
                )}
              </div>
              {outfitPreview && (
                <button
                  onClick={() => { setOutfitFile(null); setOutfitPreview(null); }}
                  className="px-3 py-2 text-xs text-red-500 border border-red-200 rounded-xl hover:bg-red-50 transition-all"
                  data-testid="create-outfit-remove"
                >
                  Remove
                </button>
              )}
            </div>
            {outfitPreview && (
              <p className="text-[10px] text-zinc-400 mt-1.5">Describe the background/setting below — AI will put your twin in this outfit in your described scene</p>
            )}
          </div>
        )}

        {/* Product Image Upload (UGC creators — put a real product in the twin's hand) */}
        {hasReference && (
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-2">
              Upload Product <span className="text-zinc-300 font-normal normal-case">(optional — bottle, box, tube, device — for UGC)</span>
            </label>
            <div className="flex gap-3">
              <div
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("border-violet-400", "bg-violet-50"); }}
                onDragLeave={(e) => { e.currentTarget.classList.remove("border-violet-400", "bg-violet-50"); }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove("border-violet-400", "bg-violet-50");
                  const file = e.dataTransfer.files?.[0];
                  if (file && file.type.startsWith("image/")) {
                    setProductFile(file);
                    setProductPreview(URL.createObjectURL(file));
                  }
                }}
                onPaste={(e) => {
                  const items = e.clipboardData?.items;
                  if (items) {
                    for (const item of items) {
                      if (item.type.startsWith("image/")) {
                        const file = item.getAsFile();
                        if (file) {
                          setProductFile(file);
                          setProductPreview(URL.createObjectURL(file));
                        }
                        break;
                      }
                    }
                  }
                }}
                tabIndex={0}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-4 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                  productPreview ? "border-violet-300 bg-violet-50" : "border-zinc-200 hover:border-violet-400 hover:bg-violet-50/40"
                }`}
                data-testid="create-product-upload-btn"
                onClick={() => productInputRef.current?.click()}
              >
                <input
                  ref={productInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setProductFile(file);
                      setProductPreview(URL.createObjectURL(file));
                    }
                  }}
                  data-testid="create-product-file-input"
                />
                {productPreview ? (
                  <div className="flex items-center gap-3">
                    <img src={productPreview} alt="Product" className="w-14 h-14 object-cover rounded-lg border border-violet-300" />
                    <div className="text-left">
                      <p className="text-sm font-medium text-violet-700">Product uploaded</p>
                      <p className="text-xs text-violet-600">Drop, paste, or click to change</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <Upload size={20} className="mx-auto text-zinc-400 mb-1" />
                    <p className="text-sm text-zinc-600 font-medium">Upload Product Image</p>
                    <p className="text-[10px] text-zinc-400 mt-0.5">Drag & drop, paste, or click to browse</p>
                  </div>
                )}
              </div>
              {productPreview && (
                <button
                  onClick={() => { setProductFile(null); setProductPreview(null); }}
                  className="px-3 py-2 text-xs text-red-500 border border-red-200 rounded-xl hover:bg-red-50 transition-all"
                  data-testid="create-product-remove"
                >
                  Remove
                </button>
              )}
            </div>
            {productPreview && (
              <p className="text-[10px] text-zinc-400 mt-1.5">AI will place your twin holding this exact product — preserving label, shape, and color</p>
            )}
          </div>
        )}

        {/* Scene Picker (only useful with a reference image) */}
        {hasReference && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                Pick a Scene <span className="text-zinc-300 font-normal normal-case">(optional — auto-fills your prompt)</span>
              </label>
              <div className="flex items-center gap-3">
                <span className="text-xs text-zinc-400">{SCENE_PROMPTS.length} presets</span>
                <button
                  type="button"
                  onClick={refreshScenes}
                  className="text-[11px] font-semibold text-[#E91E8C] hover:text-[#c91a7b] inline-flex items-center gap-1"
                  data-testid="scene-refresh-btn"
                  title="Reshuffle — unused ones first, so you keep seeing fresh vibes"
                >
                  <ArrowsClockwise size={12} weight="bold" /> Refresh
                </button>
              </div>
            </div>

            {/* Category pills */}
            <div className="flex gap-2 overflow-x-auto pb-2 mb-3 scrollbar-hide" data-testid="scene-categories">
              {orderedCategories.map((cat) => {
                const Icon = SCENE_ICON_MAP[cat.icon] || Sparkle;
                const isActive = activeCategory === cat.id;
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setActiveCategory(cat.id)}
                    data-testid={`scene-category-${cat.id}`}
                    className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                      isActive
                        ? "bg-[#E91E8C] text-white border-[#E91E8C]"
                        : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"
                    }`}
                  >
                    <Icon size={14} weight={isActive ? "fill" : "regular"} />
                    {cat.label}
                  </button>
                );
              })}
            </div>

            {/* Scene preset cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {visibleScenes.map((scene) => {
                const isUsed = usedSceneIds.includes(scene.id);
                return (
                <button
                  key={scene.id}
                  type="button"
                  onClick={() => handlePickScene(scene)}
                  data-testid={`scene-preset-${scene.id}`}
                  className={`relative text-left p-2.5 rounded-lg border-2 transition-all ${
                    pickedSceneId === scene.id
                      ? "border-[#E91E8C] bg-[#E91E8C]/5"
                      : isUsed
                        ? "border-zinc-100 bg-zinc-50/60 hover:border-zinc-300"
                        : "border-zinc-200 hover:border-zinc-300 bg-white"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-0.5">
                    <span className={`text-sm font-semibold ${isUsed && pickedSceneId !== scene.id ? "text-zinc-500" : "text-zinc-900"}`}>{scene.label}</span>
                    {pickedSceneId === scene.id ? (
                      <Check size={13} weight="bold" className="text-[#E91E8C] flex-shrink-0 mt-0.5" />
                    ) : isUsed ? (
                      <span
                        className="text-[9px] font-bold uppercase tracking-wider text-zinc-400 bg-zinc-100 rounded px-1.5 py-0.5 flex-shrink-0"
                        data-testid={`scene-used-badge-${scene.id}`}
                      >
                        Used
                      </span>
                    ) : null}
                  </div>
                  <p className="text-[11px] text-zinc-500 line-clamp-2 leading-relaxed">{scene.prompt}</p>
                </button>
                );
              })}
            </div>

            {/* Realism toggle */}
            <label
              className="flex items-start gap-3 mt-3 p-2.5 border border-zinc-200 rounded-lg cursor-pointer hover:bg-zinc-50"
              data-testid="scene-realism-toggle"
            >
              <input
                type="checkbox"
                checked={addRealism}
                onChange={(e) => setAddRealism(e.target.checked)}
                className="mt-0.5 accent-[#E91E8C]"
              />
              <div className="flex-1">
                <div className="flex items-center gap-1.5">
                  <Sparkle size={13} weight="fill" className="text-[#E91E8C]" />
                  <span className="text-sm font-semibold text-zinc-900">Add realism boost</span>
                </div>
                <p className="text-[11px] text-zinc-500 leading-relaxed mt-0.5">
                  Adds pro photography modifiers: natural skin texture, subtle film grain, mild vignetting, 4K. Recommended.
                </p>
              </div>
            </label>
          </div>
        )}

        {/* Prompt */}
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1.5">
            {hasReference ? "Describe the look you want" : "Describe your AI Twin"}
          </label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={hasReference
              ? outfitPreview
                ? "Describe the background/setting... e.g., walking down a luxury hotel hallway, soft warm lighting, fashion editorial style"
                : "e.g., wearing a luxury red dress at a sunset beach photoshoot, golden hour lighting, confident pose, fashion magazine cover style"
              : "e.g., Professional headshot of a confident woman with long brown hair, wearing a white blazer, soft studio lighting, clean background"
            }
            className="input-field text-sm min-h-[100px] resize-none"
            data-testid="generate-prompt-input"
          />
        </div>

        {/* Model Selection */}
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1.5">
            AI Model {hasReference && outfitFile && <span className="text-[#E91E8C] normal-case font-normal">(Nano Banana recommended for outfits)</span>}
            {hasReference && !outfitFile && <span className="text-[#E91E8C] normal-case font-normal">(Flux Kontext Pro recommended for faces)</span>}
          </label>
          {hasReference ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {activeModels.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setModel(m.id)}
                  className={`p-3 rounded-lg border-2 text-left transition-all relative ${
                    model === m.id ? "border-[#E91E8C] bg-[#E91E8C]/5" : "border-zinc-200 hover:border-zinc-300"
                  }`}
                  data-testid={`model-select-${m.id}`}
                >
                  <p className="text-sm font-semibold text-zinc-900">{m.name}</p>
                  <p className="text-xs text-zinc-400 mt-0.5">{m.desc}</p>
                  {m.recommended && <span className="absolute top-1.5 right-1.5 text-[9px] bg-[#E91E8C] text-white px-1.5 py-0.5 rounded-full">Best</span>}
                </button>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {activeModels.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setModel(m.id)}
                  className={`p-3 rounded-lg border-2 text-left transition-all relative ${
                    model === m.id ? "border-[#E91E8C] bg-[#E91E8C]/5" : "border-zinc-200 hover:border-zinc-300"
                  }`}
                  data-testid={`model-select-${m.id}`}
                >
                  <p className="text-sm font-semibold text-zinc-900">{m.name}</p>
                  <p className="text-xs text-zinc-400 mt-0.5">{m.desc}</p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Generate Button */}
        <button
          onClick={handleGenerate}
          disabled={generating || !prompt.trim()}
          className="btn-primary w-full flex items-center justify-center gap-2 py-3 text-base"
          data-testid="generate-image-btn"
        >
          {generating ? (
            <>
              <CircleNotch size={20} className="animate-spin" />
              {hasReference ? "Transforming... (30-90s)" : "Generating... (30-60s)"}
            </>
          ) : (
            <>
              {hasReference ? <MagicWand size={20} weight="fill" /> : <Sparkle size={20} weight="fill" />}
              {hasReference ? "Transform with AI" : "Generate AI Twin Image"}
            </>
          )}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 space-y-2" data-testid="generate-error">
          <p>{error}</p>
          {error.toLowerCase().includes("policy") || error.toLowerCase().includes("filter") || error.toLowerCase().includes("blocked") || error.toLowerCase().includes("prohibited") ? (
            <div className="bg-white/60 rounded-md p-2 text-xs text-zinc-600">
              <p className="font-semibold text-zinc-700 mb-1">Tips:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Nano Banana models (Google) have strict content filters</li>
                <li>Try <strong>Flux Kontext Pro</strong> — it has more flexible content policies</li>
                <li>Rephrase your prompt to be less specific about clothing details</li>
              </ul>
            </div>
          ) : null}
        </div>
      )}

      {result && (
        <div className="card p-4 space-y-3" data-testid="generate-result">
          <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">
            {hasReference ? "AI Transformation" : "Generated Image"}
          </p>
          {hasReference && referenceImages[0] ? (
            <div className="grid grid-cols-2 gap-3 max-w-md mx-auto">
              <div>
                <p className="text-[10px] text-zinc-400 text-center mb-1">Your Photo</p>
                <div className="rounded-lg overflow-hidden bg-zinc-100 aspect-[3/4]">
                  <img src={`${BACKEND_URL}/api/ai-twin/images/${referenceImages[0].filename}`} alt="Original" className="w-full h-full object-cover" />
                </div>
              </div>
              <div>
                <p className="text-[10px] text-zinc-400 text-center mb-1">AI Result</p>
                <div className="rounded-lg overflow-hidden bg-zinc-100 aspect-[3/4]">
                  <img
                    src={`${BACKEND_URL}/api/ai-twin/images/${result.image?.filename}`}
                    alt="Generated"
                    className="w-full h-full object-cover"
                    data-testid="generated-image-preview"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-xl overflow-hidden bg-zinc-100 max-w-xs mx-auto aspect-[3/4]">
              <img
                src={`${BACKEND_URL}/api/ai-twin/images/${result.image?.filename}`}
                alt="Generated"
                className="w-full h-full object-cover"
                data-testid="generated-image-preview"
              />
            </div>
          )}
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <button
              onClick={() => downloadFromUrl(`${BACKEND_URL}/api/ai-twin/images/${result.image?.filename}`, `ai-twin-${result.image?.id || "result"}.png`)}
              className="flex items-center gap-1.5 text-xs bg-[#E91E8C] text-white px-3 py-1.5 rounded-full font-medium hover:bg-[#d4177d] transition-all"
              data-testid="download-generated-image"
            >
              <DownloadSimple size={14} />
              Download
            </button>
            <span className="text-xs bg-green-100 text-green-700 px-2.5 py-1 rounded-full font-medium">Saved to Gallery</span>
            <span className="text-xs text-zinc-400">Model: {result.image?.model}</span>
          </div>

          {/* Product reveal video — only offered when the generation used a product image */}
          {result.has_product && (
            <div className="border-t border-zinc-100 pt-3 mt-1">
              {!revealJob && !revealLoading && (
                <div className="flex items-center justify-between gap-3 bg-violet-50 border border-violet-200 rounded-xl p-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold uppercase tracking-wider text-violet-700">Animate product reveal</p>
                    <p className="text-[11px] text-violet-600 mt-0.5">10s Seedance clip of your twin subtly presenting the product — perfect for UGC &amp; affiliate reels.</p>
                  </div>
                  <button
                    onClick={handleGenerateProductReveal}
                    className="flex-shrink-0 flex items-center gap-1.5 text-xs bg-violet-600 text-white px-3 py-2 rounded-full font-medium hover:bg-violet-700 transition-all"
                    data-testid="generate-product-reveal-btn"
                  >
                    <VideoCamera size={14} weight="fill" />
                    Generate reveal
                  </button>
                </div>
              )}
              {revealLoading && (
                <div className="flex items-center justify-center gap-2 bg-violet-50 border border-violet-200 rounded-xl p-4 text-violet-700" data-testid="reveal-loading">
                  <CircleNotch size={18} className="animate-spin" />
                  <span className="text-sm font-medium">Rendering product reveal clip (60–180s)…</span>
                </div>
              )}
              {revealJob && revealJob.status === "ready" && (
                <div className="space-y-2" data-testid="reveal-result">
                  <p className="text-xs font-bold uppercase tracking-wider text-violet-700">Product reveal clip</p>
                  <div className="rounded-xl overflow-hidden bg-zinc-100 max-w-xs mx-auto aspect-[9/16]">
                    <video
                      src={`${BACKEND_URL}${revealJob.public_path || `/api/base-videos/${revealJob.id}/file`}`}
                      controls
                      playsInline
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex items-center justify-center gap-2 flex-wrap">
                    <a
                      href={`${BACKEND_URL}${revealJob.public_path || `/api/base-videos/${revealJob.id}/file`}`}
                      download
                      className="flex items-center gap-1.5 text-xs bg-violet-600 text-white px-3 py-1.5 rounded-full font-medium hover:bg-violet-700 transition-all"
                      data-testid="download-reveal-video"
                    >
                      <DownloadSimple size={14} />
                      Download clip
                    </a>
                    <span className="text-xs bg-violet-100 text-violet-700 px-2.5 py-1 rounded-full font-medium">Saved to Base Video Library</span>
                  </div>
                </div>
              )}
              {revealJob && revealJob.status === "failed" && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700 flex items-center justify-between gap-3" data-testid="reveal-error">
                  <span>{revealJob.error || "Product reveal failed"}</span>
                  <button
                    onClick={handleGenerateProductReveal}
                    className="text-xs bg-red-600 text-white px-2.5 py-1 rounded-full font-medium hover:bg-red-700"
                  >
                    Retry
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============= MOTION CONTROL TAB =============
const MotionControlTab = ({ images }) => {
  const [refs, setRefs] = useState([]);
  const [myVideos, setMyVideos] = useState([]);
  const [refSource, setRefSource] = useState("uploaded"); // "uploaded" | "my_videos"
  const [jobs, setJobs] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  // Unified selection: either a motion_ref id or a video-from-my-library.
  // Shape: {kind: "ref"|"video", id, name, video_url?}
  const [selectedSource, setSelectedSource] = useState(null);
  const [aspectRatio, setAspectRatio] = useState("9:16");
  const [prompt, setPrompt] = useState("");
  const [model, setModel] = useState("runway-act-two-i2v");
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [uploadName, setUploadName] = useState("");
  const refInputRef = useRef(null);

  const supportsAspectRatio = model === "runway-act-two-i2v";

  const fetchRefs = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/motion-control/reference-videos`);
      setRefs(r.data.references || []);
    } catch (e) { /* non-fatal */ }
  }, []);

  const fetchMyVideos = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/my-videos`);
      // Exclude motion outputs — using one as a driving clip for another motion
      // transfer is almost never what the user wants, and creates feedback loops.
      setMyVideos((r.data.videos || []).filter((v) => v.type !== "motion"));
    } catch (e) { /* non-fatal */ }
  }, []);

  const fetchJobs = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/motion-control/jobs`);
      setJobs(r.data.jobs || []);
    } catch (e) { /* non-fatal */ }
  }, []);

  useEffect(() => {
    fetchRefs();
    fetchMyVideos();
    fetchJobs();
    const t = setInterval(fetchJobs, 5000); // poll while processing (also advances progress bar)
    return () => clearInterval(t);
  }, [fetchRefs, fetchMyVideos, fetchJobs]);

  const handleUploadRef = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      setError("Reference video must be under 50 MB");
      return;
    }
    setUploading(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", uploadName || file.name.replace(/\.[^.]+$/, ""));
      const res = await axios.post(`${API}/motion-control/reference-videos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadName("");
      if (refInputRef.current) refInputRef.current.value = "";
      await fetchRefs();
      // Auto-select the just-uploaded reference so the user doesn't have to click again
      const newRef = res.data?.reference;
      if (newRef) {
        setSelectedSource({ kind: "ref", id: newRef.id, name: newRef.name });
        setRefSource("uploaded");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteRef = async (refId) => {
    try {
      await axios.delete(`${API}/motion-control/reference-videos/${refId}`);
      if (selectedSource?.kind === "ref" && selectedSource.id === refId) setSelectedSource(null);
      await fetchRefs();
    } catch (err) {
      setError(err.response?.data?.detail || "Couldn't delete reference — try again");
    }
  };

  const handleGenerate = async () => {
    if (!selectedImage || !selectedSource) return;
    setGenerating(true); setError("");
    try {
      if (selectedSource.kind === "ref") {
        // Traditional flow — motion_refs record already exists
        const payload = {
          image_id: selectedImage.id,
          reference_id: selectedSource.id,
          model,
          prompt: prompt.trim(),
        };
        if (supportsAspectRatio) payload.aspect_ratio = aspectRatio;
        await axios.post(`${API}/motion-control/generate`, payload);
      } else {
        // User picked one of their completed videos — use /remix which downloads
        // + wraps it as a motion_ref server-side, then kicks off the transfer.
        const payload = {
          image_id: selectedImage.id,
          source_video_url: selectedSource.video_url,
          source_name: selectedSource.name || "My video",
          model,
          prompt: prompt.trim(),
        };
        if (supportsAspectRatio) payload.aspect_ratio = aspectRatio;
        await axios.post(`${API}/motion-control/remix`, payload);
      }
      await Promise.all([fetchJobs(), fetchRefs()]);
    } catch (err) {
      setError(err.response?.data?.detail || "Motion transfer failed to start");
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteJob = async (jobId) => {
    try {
      await axios.delete(`${API}/motion-control/jobs/${jobId}`);
      await fetchJobs();
    } catch (e) { /* ignore */ }
  };

  const handleDownload = (j) => {
    downloadFromUrl(`${BACKEND_URL}/api/motion-control/jobs/${j.id}/file`, `motion_${j.id.slice(0, 8)}.mp4`);
  };

  const handleSaveToVault = async (j) => {
    try {
      await axios.post(`${API}/vault/save`, {
        title: `Motion · ${j.reference_name || "untitled"}`,
        platform: "tiktok",
        category: "motion",
        video_url: `${BACKEND_URL}/api/motion-control/jobs/${j.id}/file`,
        caption: "",
        hashtags: [],
        tags: ["motion-control", j.model || ""],
        post_type: "video",
        scheduled_date: null,
        source_job_id: j.id,
      });
      setError("");
      setJobs((prev) => prev.map((x) => x.id === j.id ? { ...x, _vaulted: true } : x));
      setTimeout(() => setJobs((prev) => prev.map((x) => x.id === j.id ? { ...x, _vaulted: false } : x)), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || "Couldn't save to vault");
    }
  };

  const progressFor = (j) => {
    if (j.status === "completed") return 100;
    if (j.status === "failed") return 0;
    const start = j.created_at ? new Date(j.created_at).getTime() : Date.now();
    const elapsed = Math.max(0, (Date.now() - start) / 1000);
    const total = j.est_total_seconds || 300;
    return Math.min(95, Math.round((elapsed / total) * 100));
  };

  const etaFor = (j) => {
    if (j.status !== "processing") return null;
    const start = j.created_at ? new Date(j.created_at).getTime() : Date.now();
    const elapsed = Math.max(0, (Date.now() - start) / 1000);
    const total = j.est_total_seconds || 300;
    const remain = Math.max(0, total - elapsed);
    if (remain < 30) return "under 30s";
    if (remain < 120) return `~${Math.ceil(remain / 30) * 30}s`;
    return `~${Math.ceil(remain / 60)} min`;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="motion-control-tab">
      {/* LEFT: config */}
      <div className="lg:col-span-2 space-y-5">
        <div className="card p-4 space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block">1. Pick your AI Twin</label>
            <p className="text-[11px] text-zinc-400 mt-1 mb-3">Motion from the reference video will be transferred onto this character.</p>
            {images.length === 0 ? (
              <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-3">No AI Twin images yet — create one in the Gallery tab first.</p>
            ) : (
              <div className="grid grid-cols-6 sm:grid-cols-8 gap-2">
                {images.slice(0, 24).map((img) => (
                  <button
                    key={img.id}
                    type="button"
                    onClick={() => setSelectedImage(img)}
                    className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage?.id === img.id ? "border-[#E91E8C] ring-2 ring-[#E91E8C]/30" : "border-zinc-200 hover:border-zinc-300"
                    }`}
                    data-testid={`motion-pick-image-${img.id}`}
                  >
                    <img src={`${BACKEND_URL}/api/ai-twin/images/${img.filename}`} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="card p-4 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">2. Pick a reference / driving clip</label>
              <button
                onClick={() => refInputRef.current?.click()}
                disabled={uploading}
                className="text-[11px] font-semibold text-[#E91E8C] hover:text-[#c91a7b] flex items-center gap-1 disabled:opacity-40"
                data-testid="motion-upload-btn"
              >
                {uploading ? <CircleNotch size={11} className="animate-spin" /> : <Upload size={11} weight="bold" />}
                {uploading ? "Uploading…" : "Upload new"}
              </button>
              <input ref={refInputRef} type="file" accept="video/*" className="hidden" onChange={handleUploadRef} />
            </div>

            {/* Source tabs: uploaded refs vs user's completed videos */}
            <div className="flex items-center gap-1 mb-3 border-b border-zinc-200">
              <button
                onClick={() => setRefSource("uploaded")}
                className={`px-3 py-1.5 text-[11px] font-semibold border-b-2 transition-all ${
                  refSource === "uploaded" ? "border-[#E91E8C] text-[#E91E8C]" : "border-transparent text-zinc-500 hover:text-zinc-700"
                }`}
                data-testid="motion-source-uploaded"
              >
                Uploaded ({refs.length})
              </button>
              <button
                onClick={() => setRefSource("my_videos")}
                className={`px-3 py-1.5 text-[11px] font-semibold border-b-2 transition-all ${
                  refSource === "my_videos" ? "border-[#E91E8C] text-[#E91E8C]" : "border-transparent text-zinc-500 hover:text-zinc-700"
                }`}
                data-testid="motion-source-my-videos"
              >
                My videos ({myVideos.length})
              </button>
            </div>

            {refSource === "uploaded" ? (
              <>
                <p className="text-[11px] text-zinc-400 mb-3">Any short clip of a person moving (up to 50 MB · 30 s max). We trim longer videos automatically.</p>
                {refs.length === 0 ? (
                  <p className="text-sm text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-lg p-4 text-center">
                    No uploaded references yet — click "Upload new" above, or switch to "My videos" to use something you already made.
                  </p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {refs.map((r) => {
                      const picked = selectedSource?.kind === "ref" && selectedSource.id === r.id;
                      return (
                        <div
                          key={r.id}
                          className={`relative rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                            picked ? "border-[#E91E8C] ring-2 ring-[#E91E8C]/30" : "border-zinc-200 hover:border-zinc-300"
                          }`}
                          onClick={() => setSelectedSource({ kind: "ref", id: r.id, name: r.name })}
                          data-testid={`motion-ref-${r.id}`}
                        >
                          <video src={`${BACKEND_URL}/api/motion-control/reference-videos/${r.id}/file`} className="w-full aspect-video object-cover bg-zinc-900 pointer-events-none" muted playsInline preload="metadata" />
                          <div className="p-2 bg-white border-t border-zinc-100">
                            <p className="text-[11px] font-medium text-zinc-900 truncate">{r.name}</p>
                            <p className="text-[10px] text-zinc-400">{r.duration_seconds?.toFixed(1) || "?"}s · {(r.size_bytes/1024/1024).toFixed(1)}MB</p>
                          </div>
                          {picked && (
                            <span className="absolute top-1 left-1 bg-[#E91E8C] text-white text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded shadow">Selected</span>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteRef(r.id); }}
                            className="absolute top-1 right-1 p-1 bg-white/90 hover:bg-white rounded text-zinc-500 hover:text-red-500 shadow"
                            data-testid={`motion-ref-delete-${r.id}`}
                          >
                            <Trash size={12} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <>
                <p className="text-[11px] text-zinc-400 mb-3">Reuse any completed video as the driving clip — we'll fetch it automatically. Motion outputs are hidden to avoid feedback loops.</p>
                {myVideos.length === 0 ? (
                  <p className="text-sm text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-lg p-4 text-center">
                    You don't have any completed videos yet. Generate one from Auto Create / Lifestyle first, or upload a reference.
                  </p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[420px] overflow-y-auto pr-1">
                    {myVideos.map((v) => {
                      const picked = selectedSource?.kind === "video" && selectedSource.id === v.id;
                      const playback = v.playback_url?.startsWith("/api/") ? `${BACKEND_URL}${v.playback_url}` : (v.video_url || v.playback_url);
                      const sourceUrl = playback; // backend /remix resolves localhost for /api paths
                      return (
                        <div
                          key={`${v.type}-${v.id}`}
                          className={`relative rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                            picked ? "border-[#E91E8C] ring-2 ring-[#E91E8C]/30" : "border-zinc-200 hover:border-zinc-300"
                          }`}
                          onClick={() => setSelectedSource({ kind: "video", id: v.id, name: v.title || "My video", video_url: sourceUrl })}
                          data-testid={`motion-video-${v.id}`}
                        >
                          <video src={playback} className="w-full aspect-video object-cover bg-zinc-900 pointer-events-none" muted playsInline preload="metadata" />
                          <div className="p-2 bg-white border-t border-zinc-100">
                            <p className="text-[11px] font-medium text-zinc-900 truncate" title={v.title}>{v.title || "Untitled"}</p>
                            <p className="text-[10px] text-zinc-400 uppercase tracking-wider">{v.type?.replace("_", " ")}</p>
                          </div>
                          {picked && (
                            <span className="absolute top-1 left-1 bg-[#E91E8C] text-white text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded shadow">Selected</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="card p-4 space-y-3">
          <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block">3. Options</label>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 block mb-1">Model</label>
            <select value={model} onChange={(e) => setModel(e.target.value)} className="input-field text-sm" data-testid="motion-model">
              <option value="runway-act-two-i2v">Runway Act-Two — fastest, facial + head motion</option>
              <option value="kling-v2.6-pro-motion-control">Kling 2.6 Pro Motion Control — camera + subject (V2V)</option>
              <option value="kling-v3.0-pro-motion-control">Kling 3.0 Pro Motion Control — best quality (slower)</option>
            </select>
            <p className="text-[10px] text-zinc-400 mt-1">
              {model === "runway-act-two-i2v"
                ? "Transfers the speaker's facial expressions and head movement onto your AI Twin in its original background."
                : "Applies the camera moves and subject dynamics from the reference clip to your AI Twin. Best for full-body motion."}
            </p>
          </div>
          {supportsAspectRatio && (
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 block mb-1">Aspect Ratio</label>
              <select value={aspectRatio} onChange={(e) => setAspectRatio(e.target.value)} className="input-field text-sm" data-testid="motion-aspect-ratio">
                <option value="9:16">9:16 — Reels / TikTok / Shorts</option>
                <option value="16:9">16:9 — YouTube / landscape</option>
                <option value="1:1">1:1 — Square</option>
                <option value="4:3">4:3</option>
                <option value="3:4">3:4</option>
                <option value="21:9">21:9 — Ultra-wide</option>
              </select>
            </div>
          )}
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 block mb-1">Prompt (optional)</label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={2}
              placeholder="e.g. cinematic lighting, warm background tones, confident posture"
              className="input-field text-sm resize-none"
              data-testid="motion-prompt"
            />
          </div>
        </div>

        {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>}

        {(!selectedImage || !selectedSource) && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
            {!selectedImage && !selectedSource
              ? "👉 Pick an AI Twin above and click a reference / video below, then Generate."
              : !selectedImage
                ? "👉 Pick an AI Twin above."
                : "👉 Click a reference video (or a video under 'My videos' tab) to select it — look for the pink border, then Generate."}
          </p>
        )}

        {selectedImage && selectedSource && (
          <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 flex items-center gap-2">
            <Check size={14} weight="bold" />
            Ready: "{selectedSource.name}" → {selectedImage.filename?.slice(0, 30) || "AI Twin"}. Click Generate when ready.
          </p>
        )}

        <button
          onClick={handleGenerate}
          disabled={!selectedImage || !selectedSource || generating}
          className="w-full bg-[#E91E8C] hover:bg-[#c91a7b] text-white font-bold py-3 rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          data-testid="motion-generate-btn"
        >
          {generating ? <><CircleNotch size={18} className="animate-spin" /> Submitting…</> : <><MagicWand size={18} weight="fill" /> Generate Motion Transfer</>}
        </button>
      </div>

      {/* RIGHT: history */}
      <div className="card p-4">
        <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-3">Motion History</label>
        {jobs.length === 0 ? (
          <p className="text-sm text-zinc-400">No motion transfers yet.</p>
        ) : (
          <div className="space-y-3">
            {jobs.map((j) => (
              <div key={j.id} className="border border-zinc-200 rounded-lg overflow-hidden" data-testid={`motion-job-${j.id}`}>
                {j.status === "completed" && j.output_filename ? (
                  <video src={`${BACKEND_URL}/api/motion-control/jobs/${j.id}/file`} controls className="w-full aspect-[9/16] object-cover bg-black" />
                ) : j.status === "failed" ? (
                  <div className="aspect-[9/16] bg-red-50 flex items-center justify-center p-3">
                    <p className="text-xs text-red-600 text-center">{j.error || "Failed"}</p>
                  </div>
                ) : (
                  <div className="aspect-[9/16] bg-zinc-100 flex flex-col items-center justify-center p-3 gap-2">
                    <CircleNotch size={24} className="animate-spin text-[#E91E8C]" />
                    <p className="text-[11px] text-zinc-600 font-medium capitalize">{(j.current_step || "processing").replace(/_/g, " ")}</p>
                    {/* Progress bar driven by elapsed vs estimated total */}
                    <div className="w-full max-w-[180px]">
                      <div className="h-1.5 bg-zinc-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#E91E8C] transition-all duration-700"
                          style={{ width: `${progressFor(j)}%` }}
                        />
                      </div>
                      <p className="text-[10px] text-zinc-500 mt-1 text-center">
                        {progressFor(j)}%{etaFor(j) ? ` · ETA ${etaFor(j)}` : ""}
                      </p>
                    </div>
                  </div>
                )}
                <div className="p-2">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-medium text-zinc-900 truncate">{j.reference_name || "Motion"}</p>
                      <p className="text-[10px] text-zinc-400">
                        {new Date(j.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })} · {j.mode} · {j.resolution}
                        {j.model ? ` · ${j.model.startsWith("kling") ? "Kling" : "Wan"}` : ""}
                      </p>
                    </div>
                    <button onClick={() => handleDeleteJob(j.id)} className="p-1 text-zinc-400 hover:text-red-500" data-testid={`motion-job-delete-${j.id}`} title="Delete">
                      <Trash size={12} />
                    </button>
                  </div>
                  {j.status === "completed" && j.output_filename && (
                    <div className="flex items-center gap-1 mt-2">
                      <button
                        onClick={() => handleDownload(j)}
                        className="flex-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-700 hover:text-[#E91E8C] border border-zinc-200 hover:border-[#E91E8C]/40 rounded-md py-1.5 flex items-center justify-center gap-1 transition-colors"
                        data-testid={`motion-download-${j.id}`}
                      >
                        <DownloadSimple size={10} weight="bold" /> Download
                      </button>
                      <button
                        onClick={() => handleSaveToVault(j)}
                        disabled={j._vaulted}
                        className={`flex-1 text-[10px] font-semibold uppercase tracking-wider border rounded-md py-1.5 flex items-center justify-center gap-1 transition-colors ${
                          j._vaulted
                            ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                            : "text-zinc-700 hover:text-[#E91E8C] border-zinc-200 hover:border-[#E91E8C]/40"
                        }`}
                        data-testid={`motion-save-vault-${j.id}`}
                      >
                        {j._vaulted ? <><Check size={10} weight="bold" /> Saved</> : <><Plus size={10} weight="bold" /> To Vault</>}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============= MAIN COMPONENT =============
const TABS = [
  { id: "gallery", label: "Gallery", icon: Images },
  { id: "create", label: "Create", icon: Sparkle },
  { id: "motion", label: "Motion Control", icon: VideoCamera },
];

const AITwinGallery = () => {
  const { user } = useAuth();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeTab, setActiveTab] = useState("gallery");

  const fetchImages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ai-twin/images`);
      setImages(res.data.images);
    } catch (e) {
      console.error("Error fetching images:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <CircleNotch size={32} className="animate-spin text-[#E91E8C]" />
    </div>
  );

  return (
    <div className="space-y-5" data-testid="ai-twin-gallery-section">
      <div>
        <h2 className="text-2xl font-bold text-zinc-900">AI Twin Studio</h2>
        <p className="text-zinc-500 text-sm mt-1">Create, transform, and manage your AI Twin images</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-zinc-100 p-1 rounded-xl w-fit" data-testid="ai-twin-tabs">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? "bg-white text-zinc-900 shadow-sm"
                  : "text-zinc-500 hover:text-zinc-700"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <Icon size={16} weight={activeTab === tab.id ? "fill" : "regular"} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === "gallery" && (
        <GalleryTab
          images={images}
          selectedImage={selectedImage}
          setSelectedImage={setSelectedImage}
          fetchImages={fetchImages}
          uploading={uploading}
          setUploading={setUploading}
        />
      )}
      {activeTab === "create" && <CreateTab images={images} fetchImages={fetchImages} userIndustry={user?.industry} />}
      {activeTab === "motion" && <MotionControlTab images={images} />}
    </div>
  );
};

export default AITwinGallery;